import { useMutation, useQuery } from '@apollo/client';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { InputBase } from '@mui/material';
import Button from '@mui/material/Button';
import TablePagination from '@mui/material/TablePagination';
import Tooltip from '@mui/material/Tooltip';
import { useNavigate } from '@tanstack/react-router';
import React, { type ChangeEvent, type MouseEvent, useState } from 'react';
import { AddButton, Card, GroupsDeleteDialog, Table, Title } from 'src/Administration/Common';
import allPermissionGroup from 'src/api/permission/allPermissionGroup';
import deletePermissionGroup from 'src/api/permission/deletePermissionGroup';
import { StyledContextMenu } from 'src/components/menus/StyledContextMenu';
import type { ActionItem } from 'src/constants';
import { MENU_GROUPS } from 'src/constants';
import { useCustomToast } from 'src/hooks/menu';
import styles from './GroupsPageStyles';

const ADMIN_GROUP_ACTIONS: ActionItem[] = [
  { label: 'Edit', icon: <EditIcon style={{ color: 'currentColor' }} />, group: MENU_GROUPS.MANAGE },
  { label: 'Delete', icon: <DeleteIcon style={{ color: 'currentColor' }} />, group: MENU_GROUPS.DANGER },
];

interface PermissionGroup {
  id: string;
  name: string;
  description: string;
}

interface PermissionGroupNode {
  node: PermissionGroup;
}

interface AllPermissionGroupsData {
  allPermissionGroups: {
    edges: PermissionGroupNode[];
    totalCount: number;
  };
}

interface TableCellValue {
  value: React.ReactNode;
  display: boolean;
}

type TableRow = TableCellValue[];

interface HeadCell {
  id: string;
  numeric: boolean;
  disablePadding: boolean;
  label: string;
}

interface GroupNameProps {
  id: string;
  name: string;
}

interface MoreButtonProps {
  group: PermissionGroup;
  setClickedGroupContext: React.Dispatch<React.SetStateAction<Partial<PermissionGroup>>>;
}

function GroupName({ id, name }: GroupNameProps): React.ReactElement {
  const navigate = useNavigate();
  const classes = styles();

  const onClickGroup = (): void => {
    navigate({ to: `/systemadmin/groups/edit/${id}` });
  };

  return (
    <button
      type="button"
      className={classes.groupName}
      onClick={onClickGroup}
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        font: 'inherit',
        color: 'inherit',
        textAlign: 'left',
      }}
    >
      {name}
    </button>
  );
}

function MoreButton({ group, setClickedGroupContext }: MoreButtonProps): React.ReactElement {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const classes = styles();
  const open = Boolean(anchorEl);
  const navigate = useNavigate();

  const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const handleAction = (action: ActionItem): void => {
    if (action.label === 'Edit') {
      navigate({ to: `/systemadmin/groups/edit/${group.id}` });
    } else if (action.label === 'Delete') {
      setClickedGroupContext(group);
    }
    setAnchorEl(null);
  };

  return (
    <div>
      <Button
        className={classes.moreIcon}
        id="basic-button"
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        endIcon={<MoreVertIcon sx={{ color: 'text.primary' }} />}
      ></Button>
      <StyledContextMenu
        actions={ADMIN_GROUP_ACTIONS}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onAction={handleAction}
        testIdPrefix="cyp-admin-group-action"
      />
    </div>
  );
}

function generateRows(
  data: PermissionGroupNode[],
  _refetch: () => void,
  setClickedGroupContext: React.Dispatch<React.SetStateAction<Partial<PermissionGroup>>>,
): TableRow[] {
  return data.map((a) => [
    { value: a.node.id, display: false },
    { value: <GroupName name={a?.node.name} id={a?.node.id} />, display: true },
    { value: a.node.description, display: true },
    { value: <MoreButton group={a?.node} setClickedGroupContext={setClickedGroupContext} />, display: true },
  ]);
}

const headCells: HeadCell[] = [
  {
    id: 'name',
    numeric: false,
    disablePadding: false,
    label: 'Name',
  },
  {
    id: 'description',
    numeric: true,
    disablePadding: false,
    label: 'Description',
  },
  {
    id: '',
    numeric: true,
    disablePadding: false,
    label: '',
  },
];

const GroupsPage = (): React.ReactElement => {
  const classes = styles();
  const navigate = useNavigate();
  const { toastr } = useCustomToast();
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [page, setPage] = useState(0);
  const [searchVal, setSearchVal] = useState('');

  const {
    data: groupsList,
    fetchMore,
    loading,
    refetch,
  } = useQuery<AllPermissionGroupsData>(allPermissionGroup, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    variables: {
      first: rowsPerPage,
      last: rowsPerPage,
    },
  });

  const count = groupsList?.allPermissionGroups.totalCount ?? 0;

  const [clickedGroupContext, setClickedGroupContext] = useState<Partial<PermissionGroup>>({});
  const [deletePermissionGroupReq] = useMutation(deletePermissionGroup);

  const gotoAddGroupPage = (): void => {
    navigate({ to: '/systemadmin/groups/new' });
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangePage = (_event: MouseEvent<HTMLButtonElement> | null, newPage: number): void => {
    setPage(newPage);
  };

  const fetchMoreData = (howManyDeleted = 0): void => {
    const _count = count - howManyDeleted;
    const last = _count && (page + 1) * rowsPerPage > _count ? _count - page * rowsPerPage : rowsPerPage;

    fetchMore({
      variables: {
        first: (page + 1) * rowsPerPage,
        last: last,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previousResult;
        return fetchMoreResult;
      },
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: fetchMoreData changes on every render due to count dependency; only re-fetch when page or rowsPerPage changes
  React.useEffect(() => {
    fetchMoreData();
  }, [page, rowsPerPage]);

  const onChangeSearch = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    if (e.target.value.length === 0) {
      setPage(0);
    } else {
      fetchMore({
        variables: {
          name_Icontains: e.target.value,
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (!fetchMoreResult) return previousResult;
          return fetchMoreResult;
        },
      });
    }
    setSearchVal(e.target.value);
  };

  const handleDeleteDialogClose = (): void => {
    setClickedGroupContext({});
  };

  const handleDeleteGroup = (): void => {
    deletePermissionGroupReq({
      variables: {
        permissionsGroupId: clickedGroupContext.id,
      },
      refetchQueries: [allPermissionGroup],
    })
      .then(() => {
        toastr.success(`Group ${clickedGroupContext.name} deleted successfully`);
      })
      .catch((error: Error) => {
        toastr.error(`Error deleting group: ${error.message}`);
      })
      .finally(() => {
        refetch();
        handleDeleteDialogClose();
      });
  };

  return (
    <Card className={classes.mainCard}>
      <div className={classes.tableHeader}>
        <div className={classes.topSectionContainer}>
          <Title>Permission Groups</Title>
          <TablePagination
            sx={{ color: 'text.primary' }}
            rowsPerPageOptions={[25, 50, 100, 250]}
            component="div"
            count={count}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
          <Tooltip title="Search">
            <InputBase
              className={classes.input}
              placeholder="Search group name"
              style={{
                width: '300px',
              }}
              onChange={onChangeSearch}
              value={searchVal}
            />
          </Tooltip>
        </div>
        <div className={classes.addGroupButton}>
          <AddButton onClick={gotoAddGroupPage}>Add Group</AddButton>
        </div>
      </div>
      <div className={classes.tableContainer}>
        <Table
          checkboxVisibility
          minTableHeight={800}
          loading={loading}
          rowsPerPage={rowsPerPage}
          rows={
            groupsList ? generateRows(groupsList.allPermissionGroups.edges, fetchMoreData, setClickedGroupContext) : []
          }
          headCells={headCells}
        />
      </div>
      {clickedGroupContext.id && (
        <GroupsDeleteDialog
          name={clickedGroupContext.name}
          onClose={handleDeleteDialogClose}
          onOk={handleDeleteGroup}
        />
      )}
    </Card>
  );
};

export default GroupsPage;
